import { CompanyProfileTypeDto } from '@/api/generated/models/company-profile-type-dto';
import { RoleTypeDto } from '@/api/generated/models/role-type-dto';
import { UserStatusDto } from '@/api/generated/models/user-status-dto';
import { VacancyClosureReasonDto } from '@/api/generated/models/vacancy-closure-reason-dto';
import { VacancyProjectStatusDto } from '@/api/generated/models/vacancy-project-status-dto';
import { VacancyStatusEnumDto } from '@/api/generated/models/vacancy-status-enum-dto';
type DictionaryTranslations = Record<string, Record<string, string>> & {
  closureReasons: Record<VacancyClosureReasonDto, string>;
  companyProfiles: Record<
    (typeof CompanyProfileTypeDto)[keyof typeof CompanyProfileTypeDto],
    string
  >;
  defaultRoles: Record<(typeof RoleTypeDto)[keyof typeof RoleTypeDto], string>;
  employeeStatuses: Record<(typeof UserStatusDto)[keyof typeof UserStatusDto], string>;
  projectStatus: Record<
    (typeof VacancyProjectStatusDto)[keyof typeof VacancyProjectStatusDto],
    string
  >;
  vacancyStatus: Record<(typeof VacancyStatusEnumDto)[keyof typeof VacancyStatusEnumDto], string>;
};

const dictionaryTranslations: DictionaryTranslations = {
  closureReasons: {
    [VacancyClosureReasonDto.CandidateNotFound]: 'Candidates not found',
    [VacancyClosureReasonDto.CandidatesSigned]: 'Candidates signed',
    [VacancyClosureReasonDto.PriceTooHigh]: 'Price too high',
    [VacancyClosureReasonDto.ReasonUnknown]: 'Reason unknown',
  },
  companyProfiles: {
    AFFILIATES: `companyDetails.companyProfile.${CompanyProfileTypeDto.Affiliates}`,
    EXTERNAL: `companyDetails.companyProfile.${CompanyProfileTypeDto.External}`,
    INTERNAL: `companyDetails.companyProfile.${CompanyProfileTypeDto.Internal}`,
  },
  contractTypes: {
    B2B: 'contractTypes.B2B',
    MC: 'contractTypes.MC',
    OTHER: 'contractTypes.OTHER',
    OUT: 'contractTypes.OUT',
    UOD: 'contractTypes.UOD',
    UOD0: 'contractTypes.UOD0',
    UOD20: 'contractTypes.UOD20',
    UOD50: 'contractTypes.UOD50',
    UOP: 'contractTypes.UOP',
    UOP50: 'contractTypes.UOP50',
    UZ: 'contractTypes.UZ',
    UZ0: 'contractTypes.UZ0',
    UZ20: 'contractTypes.UZ20',
    UZ50: 'contractTypes.UZ50',
  },
  defaultRoles: {
    ADMIN: `defaultRoles.${RoleTypeDto.Admin}`,
    EMPLOYEE: `defaultRoles.${RoleTypeDto.Employee}`,
    EXTERNAL_PARTNER: `defaultRoles.${RoleTypeDto.ExternalPartner}`,
    GROUP_EXTERNAL_PARTNER: `defaultRoles.${RoleTypeDto.GroupExternalPartner}`,
    LOCAL_RESOURCE_MANAGER: `defaultRoles.${RoleTypeDto.LocalResourceManager}`,
    PRE_SALES: `defaultRoles.${RoleTypeDto.PreSales}`,
    REQUESTER: `defaultRoles.${RoleTypeDto.Requester}`,
  },
  employeeStatuses: {
    ACTIVE: `companyDetails.employeeStatus.${UserStatusDto.Active}`,
    INACTIVE: `companyDetails.employeeStatus.${UserStatusDto.Inactive}`,
    WAITING_FOR_APPROVAL: `companyDetails.employeeStatus.${UserStatusDto.WaitingForApproval}`,
  },
  evaluationType: {
    EVALUATION: 'employeesEvaluations.regularEvaluation',
    MICRO_EVALUATION: 'employeesEvaluations.feedback',
  },
  languageProficiences: {
    ADVANCED: 'profile.forms.language.advanced',
    BASIC: 'profile.forms.language.basic',
    BILINGUAL: 'profile.forms.language.bilingual',
    INTERMEDIATE: 'profile.forms.language.intermediate',
    PROFESSIONAL: 'profile.forms.language.professional',
  },
  projectStatus: {
    [VacancyProjectStatusDto.Active]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.Active,
    ]}`,
    [VacancyProjectStatusDto.Archived]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.Archived,
    ]}`,
    [VacancyProjectStatusDto.ClosedLost]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.ClosedLost,
    ]}`,
    [VacancyProjectStatusDto.ClosedWon]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.ClosedWon,
    ]}`,
    [VacancyProjectStatusDto.NoSalesforceId]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.NoSalesforceId,
    ]}`,
    [VacancyProjectStatusDto.OnHold]: `companyDetails.projectStatus.${[
      VacancyProjectStatusDto.OnHold,
    ]}`,
  },
  salaryChangeRequestRateKind: {
    GROSS: 'dashboard.salaryChangeRequestModal.gross',
    NET: 'dashboard.salaryChangeRequestModal.net',
  },
  salaryChangeRequestRateType: {
    HOURLY: 'dashboard.salaryChangeRequestModal.hourly',
    MONTHLY: 'dashboard.salaryChangeRequestModal.monthly',
  },
  vacancyAttendance: {
    HYBRID: 'views.vacancies.attendance.HYBRID',
    OFF_SITE: 'views.vacancies.attendance.OFF_SITE',
    ON_SITE: 'views.vacancies.attendance.ON_SITE',
  },
  vacancyCandidateStatus: {
    APPROVED: 'vacancyCandidate.status.approved',
    ARRANGING_INTERVIEW: 'vacancyCandidate.status.arrangingInterview',
    ASSIGNED: 'vacancyCandidate.status.assigned',
    AWAITING_VERIFICATION: 'vacancyCandidate.status.awaitingVerification',
    CLOSED: 'vacancyCandidate.status.closed',
    CONFIRMED: 'vacancyCandidate.status.confirmed',
    CONTRACTED: 'vacancyCandidate.status.contracted',
    CV_SCREENING: 'vacancyCandidate.status.cvScreening',
    EVALUATION: 'vacancyCandidate.status.evaluation',
    INITIALLY_ACCEPTED: 'vacancyCandidate.status.initialAccept',
    INTERVIEW_SET: 'vacancyCandidate.status.interviewSet',
    NEW: 'vacancyCandidate.status.new',
    REASSIGNED: 'vacancyCandidate.status.reassigned',
    REJECTED: 'vacancyCandidate.status.rejected',
  },
  vacancyRequest: {
    DELIVERY: 'views.vacancies.request.DELIVERY',
    PRE_SALES: 'views.vacancies.request.PRE_SALES',
  },
  vacancyStatus: {
    [VacancyStatusEnumDto.Archived]: `views.vacancies.status.${VacancyStatusEnumDto.Archived}`,
    [VacancyStatusEnumDto.Closed]: `views.vacancies.status.${VacancyStatusEnumDto.Closed}`,
    [VacancyStatusEnumDto.OnHold]: `views.vacancies.status.${VacancyStatusEnumDto.OnHold}`,
    [VacancyStatusEnumDto.Open]: `views.vacancies.status.${VacancyStatusEnumDto.Open}`,
  },
  vacancyVisibility: {
    CLOSED: 'views.vacancies.notVisible',
    OPEN: 'views.vacancies.visible',
  },
};
export default dictionaryTranslations;
